import React, { useEffect, useMemo, useState } from 'react';
import { Box, Alert, Button, Tabs, Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useEntity, useEntityTypeWithSchemas } from '@/hooks';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';
import {
  entityDetailRendererRegistry,
  createGenericDetailRenderer,
  type EntityDetailContext,
} from '@/components/entities';

interface EntityDetailProps {
  entityId: string;
  entityType: string;
}

export const EntityDetail: React.FC<EntityDetailProps> = ({ entityId, entityType }) => {
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const { data: entity, isLoading, error, refetch } = useEntity(entityId, entityType);
  const { data: entityConfig, isLoading: configLoading } = useEntityTypeWithSchemas(entityType);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Get the appropriate renderer for this entity type
  const renderer = useMemo(() => {
    const rendererFactory = entityDetailRendererRegistry.get(entityType);
    // TODO: GH-ADVSEC-FIX (alert 96) - validate retrieved value is a function before invoking
    // to satisfy js/unvalidated-dynamic-method-call: guards against prototype-polluted Maps
    if (typeof rendererFactory === 'function') {
      return rendererFactory();
    }
    // Fallback to generic renderer
    return createGenericDetailRenderer();
  }, [entityType]);

  // Generate tabs from the renderer
  const tabs = useMemo(() => {
    if (!entity || !entityConfig) return [];

    const context: EntityDetailContext = {
      entity,
      entityType,
      entityConfig,
      onRefetch: refetch,
    };

    return renderer.renderTabs(context) || [];
  }, [entity, entityType, entityConfig, refetch, renderer]);

  // Sync tab query param to selected tab index
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabs.length > 0) {
      const idx = tabs.findIndex(
        (t) => t.label.toLowerCase().replace(/\s+/g, '-') === tabParam.toLowerCase(),
      );
      if (idx >= 0) setTabValue(idx);
    }
  }, [searchParams, tabs]);

  if (isLoading || configLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Error loading entity: {error.message}
      </Alert>
    );
  }

  if (!entity) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Entity not found
      </Alert>
    );
  }

  if (tabs.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No content available for this entity type
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        {tabs.map((tab, index) => (
          <Tab key={index} label={tab.label} />
        ))}
      </Tabs>

      {tabs.map((tab, index) => (
        <Box key={index} hidden={tabValue !== index}>
          {tabValue === index && tab.content}
        </Box>
      ))}
    </Box>
  );
};
